/**
 * Google・Apple でログイン／登録 のボタン（#112、docs/43、Figma「画面の案」。文は contract の OAUTH_MSG＝docs/43 §12）。
 *   並び：メール・パスワード → ログイン → リンク →「または」→ Google → Apple（パスキーは2回目で Google の上に）。
 *   見た目は各社の決まり：Google＝白地・枠 #DADCE0・正式の「G」、Apple＝黒地・白文字・りんご。高さはほかのボタンと同じ（PC 44／スマホ 56）。
 *   この製品で使える外部だけ出す（GET /auth/oauth/providers。鍵が無ければ「または」ごと出さない）。
 *   押すと API の start へ移る（<a>：フォームの送信にしない＝CSP の form-action が行き先の Google・Apple を止めないように）
 */
import { type Industry, OAUTH_MSG, OAUTH_PROVIDERS, type OAuthAvailability, type OAuthIntent, type OAuthProvider } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { productHeaders } from "../lib/product.js";

/** 戻ったあとに開く画面（招待のリンクなど、クエリつきの戻り先はサーバーに渡さずブラウザに置く） */
const AFTER_KEY = "gb.oauth.after";
/** このサイトの中のパスだけ（「//」「/\」で始まるもの・バックスラッシュ入りはほかのサイトになりうるので弾く） */
const sameSitePath = (p: string | null | undefined): p is string => !!p && /^\/(?![/\\])/.test(p) && !p.includes("\\");

export function setOAuthAfter(path: string | null): void {
  try {
    if (sameSitePath(path)) sessionStorage.setItem(AFTER_KEY, path);
    else sessionStorage.removeItem(AFTER_KEY);
  } catch {
    /* 使えないブラウザ（プライベート）では覚えない */
  }
}

export function takeOAuthAfter(): string | null {
  try {
    const v = sessionStorage.getItem(AFTER_KEY);
    sessionStorage.removeItem(AFTER_KEY);
    return sameSitePath(v) ? v : null;
  } catch {
    return null;
  }
}

/** 認可の画面へ移る URL。開発で ?product= を指しているときは製品も付ける（本番は Host で決まる） */
export function oauthStartUrl(provider: OAuthProvider, intent: OAuthIntent, opts: { remember?: boolean; industry?: Industry } = {}): string {
  const q = new URLSearchParams({ intent });
  if (opts.remember === false) q.set("remember", "0");
  // 会員登録の画面で選んだ業種（#142 段4）。API が製品に合うかを見て、戻りで会社を作るときに使う
  if (opts.industry) q.set("industry", opts.industry);
  const p = productHeaders()["x-product"];
  if (p) q.set("product", p);
  return `/api/v1/auth/oauth/${provider}/start?${q.toString()}`;
}

/** この製品で使える外部（ログイン前の画面） */
export function useOAuthProviders() {
  return useQuery({
    queryKey: ["oauth-providers"],
    queryFn: () => api<OAuthAvailability>("GET", "/auth/oauth/providers"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** ロゴ（白地のボタン・一覧には黒いりんご、黒地のボタンには白いりんご） */
export function OAuthLogo({ provider, onDark = false, size = 20 }: { provider: OAuthProvider; onDark?: boolean; size?: number }) {
  const src = provider === "google" ? "/oauth/google.svg" : onDark ? "/oauth/apple-white.svg" : "/oauth/apple-black.svg";
  return <img src={src} alt="" width={size} height={size} className="oauthlogo" aria-hidden="true" />;
}

/** before：「または」のすぐ下に置くもの（ログインの画面の「パスキーでログイン」、#113。Figma の並び＝パスキー → Google → Apple） */
export function OAuthButtons({ intent, remember, after, before, industry }: { intent: "login" | "signup"; remember?: boolean; after?: string; before?: React.ReactNode; /** 会員登録の画面の業種（#142 段4） */ industry?: Industry }) {
  const q = useOAuthProviders();
  const list = OAUTH_PROVIDERS.filter((p) => q.data?.[p]);
  if (list.length === 0 && !before) return null;
  return (
    <div className="oauth" data-testid="oauth-buttons">
      <div className="oauthor" role="separator" aria-label={OAUTH_MSG.or}>
        <span>{OAUTH_MSG.or}</span>
      </div>
      {before}
      {list.map((p) => (
        <a key={p} className={`btn oauthbtn ${p}`} href={oauthStartUrl(p, intent, { ...(remember === undefined ? {} : { remember }), ...(industry ? { industry } : {}) })} onClick={() => setOAuthAfter(after ?? null)} data-testid={`oauth-${p}`}>
          <OAuthLogo provider={p} onDark={p === "apple"} />
          <span>{intent === "signup" ? OAUTH_MSG.signupWith(p) : OAUTH_MSG.loginWith(p)}</span>
        </a>
      ))}
    </div>
  );
}
